import type {
  AdminCaseQueueItem,
  AdminCaseQueueQuery,
  AuthenticatedAdmin,
  UpdatePlayerCaseRequest,
} from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { asMoment } from "../format.js";

type Filters = Pick<AdminCaseQueueQuery, "overdue" | "priority" | "scope" | "status" | "type">;

const INITIAL_FILTERS: Filters = {
  overdue: false,
  priority: undefined,
  scope: "mine",
  status: "active",
  type: undefined,
};

const label = (value: string) => value.replace("_", " ");
const isOverdue = (item: AdminCaseQueueItem) =>
  item.slaAt !== null &&
  ["open", "in_progress"].includes(item.status) &&
  new Date(item.slaAt).getTime() <= Date.now();

export function Cases({ admin }: { admin: AuthenticatedAdmin }) {
  const [cases, setCases] = useState<AdminCaseQueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [total, setTotal] = useState(0);
  const mayWrite = can(admin.role, "cases.write");

  const load = useCallback(async (active: Filters, before?: string, signal?: AbortSignal) => {
    setPending(true);
    setError(null);
    try {
      const page = await api.caseQueue({
        ...active,
        ...(before ? { before } : {}),
        ...(signal ? { signal } : {}),
      });
      setCases((current) => (before ? [...(current ?? []), ...page.cases] : page.cases));
      setNextCursor(page.nextCursor);
      setTotal(page.total);
    } catch (caught) {
      if (!signal?.aborted)
        setError(caught instanceof ApiError ? caught.message : "Could not load the case queue.");
    } finally {
      if (!signal?.aborted) setPending(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(filters, undefined, controller.signal);
    return () => controller.abort();
  }, [filters, load]);

  function chooseQueue(scope: Filters["scope"], overdue = false) {
    setFilters((current) => ({ ...current, overdue, scope, status: "active" }));
  }

  async function update(item: AdminCaseQueueItem, change: UpdatePlayerCaseRequest) {
    setPending(true);
    setError(null);
    try {
      await api.updatePlayerCase(item.userId, item.id, change);
      await load(filters);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "That case change did not go through.",
      );
      setPending(false);
    }
  }

  function openPlayer(item: AdminCaseQueueItem) {
    window.location.href = `${window.location.pathname}?screen=players&player=${encodeURIComponent(item.userId)}&tab=cases`;
  }

  return (
    <section className="body case-queue">
      <fieldset aria-label="Case queues" className="case-queue-quick">
        <button
          aria-pressed={filters.scope === "mine" && !filters.overdue}
          className="btn btn-sm btn-quiet"
          onClick={() => chooseQueue("mine")}
          type="button"
        >
          Mine
        </button>
        <button
          aria-pressed={filters.scope === "unassigned" && !filters.overdue}
          className="btn btn-sm btn-quiet"
          onClick={() => chooseQueue("unassigned")}
          type="button"
        >
          Unassigned
        </button>
        <button
          aria-pressed={filters.overdue}
          className="btn btn-sm btn-quiet"
          onClick={() => chooseQueue("all", true)}
          type="button"
        >
          Overdue
        </button>
        <button
          aria-pressed={filters.scope === "all" && !filters.overdue}
          className="btn btn-sm btn-quiet"
          onClick={() => chooseQueue("all")}
          type="button"
        >
          All
        </button>
        <span className="spacer" />
        <span className="label-13 faint">{total} matching cases</span>
      </fieldset>

      <div className="card case-queue-filters">
        <label className="field">
          <span>Status</span>
          <select
            className="input"
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                overdue: false,
                status: event.target.value as Filters["status"],
              }))
            }
            value={filters.status}
          >
            <option value="active">Active</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="field">
          <span>Type</span>
          <select
            className="input"
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                type: (event.target.value || undefined) as Filters["type"],
              }))
            }
            value={filters.type ?? ""}
          >
            <option value="">All types</option>
            <option value="support">Support</option>
            <option value="kyc">KYC</option>
            <option value="rg">Responsible Gaming</option>
          </select>
        </label>
        <label className="field">
          <span>Priority</span>
          <select
            className="input"
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                priority: (event.target.value || undefined) as Filters["priority"],
              }))
            }
            value={filters.priority ?? ""}
          >
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </label>
      </div>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}
      {cases === null ? <p className="empty">Loading case queue…</p> : null}
      {cases?.length === 0 ? <p className="empty">No cases match this queue.</p> : null}
      {cases?.length ? (
        <div className="card table-scroll">
          <table className="mobile-cards case-queue-table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Player</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Owner</th>
                <th>SLA</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {cases.map((item) => (
                <tr className={isOverdue(item) ? "case-is-overdue" : undefined} key={item.id}>
                  <td data-label="Case">
                    <strong className="case-subject">{item.subject}</strong>
                    <small className="case-updated faint">Updated {asMoment(item.updatedAt)}</small>
                  </td>
                  <td data-label="Player">
                    <button
                      className="link-button case-player"
                      onClick={() => openPlayer(item)}
                      type="button"
                    >
                      {item.player.displayName ?? item.player.email}
                    </button>
                    {item.player.displayName ? (
                      <small className="case-player-email faint">{item.player.email}</small>
                    ) : null}
                  </td>
                  <td data-label="Type">
                    <span className={`badge badge-type-${item.type}`}>{label(item.type)}</span>
                  </td>
                  <td data-label="Priority">
                    <span className={`badge badge-${item.priority}`}>{label(item.priority)}</span>
                  </td>
                  <td data-label="Status">
                    <select
                      aria-label={`Status for ${item.subject}`}
                      className="input input-compact"
                      disabled={!mayWrite || pending}
                      onChange={(event) =>
                        update(item, {
                          status: event.target.value as UpdatePlayerCaseRequest["status"],
                        })
                      }
                      value={item.status}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                  <td data-label="Owner">{item.owner?.email ?? "Unassigned"}</td>
                  <td data-label="SLA">
                    <span className={isOverdue(item) ? "case-overdue" : undefined}>
                      {item.slaAt ? asMoment(item.slaAt) : "No SLA"}
                    </span>
                  </td>
                  <td className="row-actions" data-label="Actions">
                    {mayWrite ? (
                      <button
                        className="btn btn-sm btn-quiet"
                        disabled={pending}
                        onClick={() =>
                          update(item, { ownerId: item.owner?.id === admin.id ? null : admin.id })
                        }
                        type="button"
                      >
                        {item.owner?.id === admin.id ? "Unassign" : "Take ownership"}
                      </button>
                    ) : null}
                    <button
                      className="btn btn-sm btn-quiet"
                      onClick={() => openPlayer(item)}
                      type="button"
                    >
                      Open player
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {nextCursor ? (
        <button
          className="btn btn-quiet load-more-button"
          disabled={pending}
          onClick={() => load(filters, nextCursor)}
          type="button"
        >
          Load more
        </button>
      ) : null}
    </section>
  );
}
